import { useEffect, useMemo, useState } from 'react';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
  CustomFieldExtensionSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  TextField,
  FormHelperText,
  Link,
} from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { useNobl9Metadata } from '@internal/plugin-nobl9-metadata-frontend';
import { renderLoadingAdornment } from './fieldUtils';

export const Nobl9ServiceFieldSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

interface Props extends FieldExtensionComponentProps<string> {}

type ServiceOption = {
  name: string;
  displayName: string;
};

const NOBL9_SERVICE_SLUG_PATTERN = /^[a-z0-9]+[a-z0-9-]*[a-z0-9]+$/;

export const Nobl9ServiceFieldComponent = ({
  onChange,
  formData,
  rawErrors,
  schema,
}: Props) => {
  const { services, state, error } = useNobl9Metadata();
  const [value, setValue] = useState(formData || '');

  const selectedProjectName: string | undefined = (window as any)
    .__nobl9SelectedProjectName;
  // We intentionally use the project slug (name) as the linkage key because services.projectId is derived from upstream "project" (slug).
  const selectedProjectLinkageKey: string | undefined =
    (window as any).__nobl9SelectedProjectId || selectedProjectName;

  const filteredServices = useMemo(() => {
    const filtered = selectedProjectLinkageKey
      ? services.filter(
          s =>
            s.projectId === selectedProjectLinkageKey ||
            s.projectId === selectedProjectName,
        )
      : services;
    // Map to include both name (for submission) and displayName (for display)
    return filtered.map(s => ({
      name: s.name,
      displayName: s.displayName || s.name,
    })) as ServiceOption[];
  }, [services, selectedProjectLinkageKey, selectedProjectName]);

  const selectedValue = useMemo(() => {
    if (!value) return null;
    return filteredServices.find(s => s.name === value) || value;
  }, [filteredServices, value]);

  const normalizedSubmittedValue = useMemo(() => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return '';

    const normalizedValue = trimmedValue.toLowerCase().replace(/\s+/g, '-');
    return NOBL9_SERVICE_SLUG_PATTERN.test(normalizedValue)
      ? normalizedValue
      : '';
  }, [value]);

  useEffect(() => {
    // Submit normalized slug values only, while preserving typed text in the UI.
    onChange(normalizedSubmittedValue);
  }, [normalizedSubmittedValue, onChange]);

  const loading = state === 'loading' || state === 'refreshing';

  useEffect(() => {
    // Removed ready-state diagnostics
  }, [
    loading,
    services,
    filteredServices,
    selectedProjectName,
    selectedProjectLinkageKey,
  ]);

  const rawDescription: string = (schema as any)?.description || '';
  const descriptionNodes = useMemo(() => {
    if (!rawDescription) return null;
    const parts: (string | JSX.Element)[] = [];
    const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    let lastIndex = 0;
    for (const m of rawDescription.matchAll(regex)) {
      if (m.index! > lastIndex)
        parts.push(rawDescription.slice(lastIndex, m.index!));
      parts.push(
        <Link
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          color="inherit"
        >
          {m[1]}
        </Link>,
      );
      lastIndex = (m.index || 0) + m[0].length;
    }
    if (lastIndex < rawDescription.length)
      parts.push(rawDescription.slice(lastIndex));
    return parts;
  }, [rawDescription]);
  const statusLine = useMemo(() => {
    if (loading) return 'Loading services…';
    if (error) return 'Error loading services';
    if (selectedProjectLinkageKey && !filteredServices.length)
      return 'No services for selected project';
    return '';
  }, [loading, error, selectedProjectLinkageKey, filteredServices.length]);
  return (
    <FormControl
      fullWidth
      margin="dense"
      error={!!rawErrors?.length || !!error}
    >
      <Autocomplete
        freeSolo
        disabled={loading}
        options={filteredServices}
        getOptionLabel={opt =>
          typeof opt === 'string' ? opt : opt.displayName
        }
        value={selectedValue}
        onChange={(_e, newValue) => {
          if (!newValue) {
            setValue('');
            return;
          }

          if (typeof newValue === 'string') {
            setValue(newValue);
            return;
          }

          setValue(newValue.name);
        }}
        onInputChange={(_e, inputValue, reason) => {
          if (reason === 'input') {
            setValue(inputValue);
          }
        }}
        loading={loading}
        isOptionEqualToValue={(opt, val) =>
          typeof opt === 'object' && typeof val === 'object'
            ? opt.name === val.name
            : opt === val
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Nobl9 Service Name"
            placeholder={
              selectedProjectName
                ? `Services for ${selectedProjectName}`
                : 'Select a project first or type…'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: renderLoadingAdornment(params, loading),
            }}
          />
        )}
        filterSelectedOptions
        autoHighlight
        clearOnBlur={false}
        openOnFocus
        noOptionsText={useMemo(() => {
          if (loading) return 'Loading…';
          if (selectedProjectLinkageKey)
            return 'No matching services for project';
          return 'Select a project first';
        }, [loading, selectedProjectLinkageKey])}
      />
      {(descriptionNodes || statusLine) && (
        <FormHelperText component="div" style={{ whiteSpace: 'pre-line' }}>
          {descriptionNodes}
          {descriptionNodes && statusLine ? '\n' : ''}
          {statusLine}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const Nobl9ServiceFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Nobl9_ServiceSelect',
    component: Nobl9ServiceFieldComponent,
    schema: Nobl9ServiceFieldSchema,
  }),
);

export default Nobl9ServiceFieldExtension;
